import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import {
    markNotificationAsRead,
    markNotificationAsUnread,
    markNotificationAsSeen,
    markAllNotificationsAsSeen
} from '../services/usersApi';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    // Esto será para las 5 notificaciones no leídas más recientes para el dropdown de la campana
    const [notifications, setNotifications] = useState([]);
    // Esto será el contador total de no leídas (puede ser un número o '5+')
    const [unseenCount, setUnseenCount] = useState(0); // Cambiado de unreadCount a unseenCount
    const [notificationsLoading, setNotificationsLoading] = useState(true);

    // Estado para controlar si el dropdown de la campana está abierto
    const [isBellDropdownOpen, setIsBellDropdownOpen] = useState(false);

    useEffect(() => {
        if (!user || authLoading) {
            setNotifications([]);
            setUnseenCount(0);
            setNotificationsLoading(false);
            return;
        }

        setNotificationsLoading(true);
        const userNotificationsRef = collection(db, `users/${user}/notifications`);

        // 1. Suscripción para las 6 notificaciones NO VISTAS más recientes para el dropdown de la campana
        // La idea es traer 6 para poder determinar si hay '5+'
        const qForBell = query(
            userNotificationsRef,
            where('seen', '==', false), // Filtra por no vistas
            orderBy('timestamp', 'desc'),
            limit(6) // Trae hasta 6 para la lógica de '5+'
        );

        const unsubscribeBell = onSnapshot(qForBell, (snapshot) => {
            const fetchedNotifications = [];
            snapshot.forEach((doc) => {
                fetchedNotifications.push({ id: doc.id, ...doc.data() });
            });

            // Actualiza las notificaciones del dropdown (máximo 5)
            setNotifications(fetchedNotifications.slice(0, 5));

            // Calcula el unseenCount: si hay 6 o más, muestra '5+'
            if (fetchedNotifications.length >= 6) {
                setUnseenCount('5+');
            } else {
                setUnseenCount(fetchedNotifications.length);
            }

            setNotificationsLoading(false);
        }, (error) => {
            console.error("Error listening to bell notifications:", error);
            //toast.error("Error al cargar notificaciones en tiempo real para la campana.");
            setNotificationsLoading(false);
        });


        // Limpiar los listeners al desmontar el componente o cambiar el usuario
        return () => {
            unsubscribeBell();
        };
    }, [user, authLoading]);

    // Función para marcar como leída (usa el método del backend)
    const markAsRead = useCallback(async (notificationId) => {
        try {
            await markNotificationAsRead(notificationId);
            // La suscripción de Firestore se encargará de actualizar el estado local automáticamente
        } catch (error) {
            console.error("Error marking notification as read via backend:", error);
            toast.error("Error al marcar como leída.");
        }
    }, []);

    // Función para marcar como no leída (usa el método del backend)
    const markAsUnread = useCallback(async (notificationId) => {
        try {
            await markNotificationAsUnread(notificationId);
            // La suscripción de Firestore se encargará de actualizar el estado local automáticamente
        } catch (error) {
            console.error("Error marking notification as unread via backend:", error);
            toast.error("Error al marcar como no leída.");
        }
    }, []);

    // Nueva función para marcar como vista (usa el método del backend)
    const markAsSeen = useCallback(async (notificationId) => {
        try {
            await markNotificationAsSeen(notificationId);
            // La suscripción de Firestore se encargará de actualizar el estado local automáticamente
        } catch (error) {
            console.error("Error marking notification as seen via backend:", error);
            // No mostrar un toast aquí, ya que podría ocurrir en segundo plano masivamente.
        }
    }, []);

    // Nuevo método para marcar todas las notificaciones no vistas como vistas
    // Este se llamará desde UserNotifications.jsx y NotificationBell.jsx
    const markAllUnseenAsSeen = useCallback(async () => {
        try {
            await markAllNotificationsAsSeen();
        } catch (error) {
            console.error("Error marking all unseen notifications as seen:", error);
            toast.error("Error al marcar notificaciones como vistas.");
        }

    }, [notifications, markAsSeen]); // Depende de las notificaciones actuales y de markAsSeen


    return (
        <NotificationContext.Provider value={{
            notifications,
            unseenCount, // Cambiado de unreadCount
            notificationsLoading,
            markAsRead,
            markAsUnread,
            markAsSeen,
            markAllUnseenAsSeen, // Exponemos este método
            setIsBellDropdownOpen // Exponemos el setter para la campana
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => useContext(NotificationContext);